import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import {
  Search,
  MapPin,
  Star,
  ArrowUpRight,
  ArrowDownRight,
  X,
  Home,
  LayoutGrid,
  List,
  Scale,
  Sparkles,
  Check,
  Plus,
  Coins,
  Dices,
  RotateCcw,
  User,
  LogOut,
  ShieldCheck,
  Settings,
  Trophy,
  Briefcase,
} from "lucide-react";
import { casinoAPI } from "./services/api";
import { useAuth } from "./context/AuthContext";
import ProfileModal from "./components/ProfileModal";
import LegalModal from "./components/LegalModal";
import { DEMO_CASINOS } from "./data/demoCasinos";

const locations = [
  { value: "All Locations", label: "All Locations (US)", status: "active" },
  {
    value: "Atlantic City, NJ",
    label: "Atlantic City, NJ (Live Data)",
    status: "active",
  },
  {
    value: "Las Vegas, NV",
    label: "Las Vegas, NV (Coming Soon)",
    status: "upcoming",
  },
  {
    value: "Reno, NV",
    label: "Reno, NV (Coming Soon)",
    status: "upcoming",
  },
  {
    value: "Biloxi, MS",
    label: "Biloxi, MS (Coming Soon)",
    status: "upcoming",
  },
  {
    value: "Detroit, MI",
    label: "Detroit, MI (Coming Soon)",
    status: "upcoming",
  },
];

function CasinoFigures({ onBackToLanding, isDemo = false, onSignUp }) {
  const { user, signOut, isAdmin } = useAuth();
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("All Locations");
  
  // Available periods (loaded dynamically from database)
  const [availablePeriods, setAvailablePeriods] = useState({
    years: ["2026", "2025", "2024", "2023", "2022"],
    months: [],
  });
  const [selectedTimePeriod, setSelectedTimePeriod] = useState("2026-08");
  const [perspective, setPerspective] = useState("player"); // "player" | "business"
  const [selectedCasino, setSelectedCasino] = useState(null);
  const [detailedCasino, setDetailedCasino] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [sortBy, setSortBy] = useState("highestRtp");
  const [viewMode, setViewMode] = useState("cards");
  const [comparisonIds, setComparisonIds] = useState([]);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalModalTab, setLegalModalTab] = useState("terms");

  // API state
  const [casinos, setCasinos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Ref to maintain search input focus
  const searchInputRef = useRef(null);
  const isInitialLoad = useRef(true);

  // Load available periods on mount
  useEffect(() => {
    async function loadPeriods() {
      try {
        const data = await casinoAPI.getPeriods();
        if (data && data.years && data.months && data.months.length > 0) {
          setAvailablePeriods(data);
          if (data.latestMonth) {
            setSelectedTimePeriod(data.latestMonth.value);
          }
        }
      } catch (err) {
        console.warn("Could not load dynamic periods:", err);
      }
    }
    loadPeriods();
  }, []);

  const getPeriodLabel = (periodValue) => {
    if (periodValue === "All Years") return "All Years (Aggregated)";
    const m = availablePeriods.months?.find((x) => x.value === periodValue);
    if (m) return `${m.label} (Monthly)`;
    return `Fiscal Year ${periodValue}`;
  };

  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return "$0";
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${Number(value).toLocaleString()}`;
  };

  const formatNumber = (value) => {
    if (!value || isNaN(value)) return "0";
    return new Intl.NumberFormat("en-US").format(value);
  };

  // Debounce search term - wait for user to finish typing (600ms)
  useEffect(() => {
    if (searchTerm === "") {
      setDebouncedSearchTerm("");
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 600);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch casinos from API or safe demo dataset
  const fetchCasinos = useCallback(async () => {
    if (isDemo) {
      setCasinos(DEMO_CASINOS);
      setLoading(false);
      setIsSearching(false);
      return;
    }
    try {
      if (isInitialLoad.current) {
        setLoading(true);
      } else {
        setIsSearching(true);
      }
      setError(null);

      const filters = {
        search: debouncedSearchTerm || undefined,
        location:
          selectedLocation !== "All Locations" ? selectedLocation : undefined,
        sortBy: sortBy,
        order: "desc",
        limit: 50,
      };

      if (selectedTimePeriod.includes("-")) {
        filters.period = selectedTimePeriod;
      } else if (selectedTimePeriod !== "All Years") {
        filters.year = selectedTimePeriod;
      }

      const response = await casinoAPI.getCasinos(filters);
      setCasinos(response.casinos || []);

      if (isInitialLoad.current) {
        isInitialLoad.current = false;
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching casinos:", err);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [debouncedSearchTerm, selectedLocation, selectedTimePeriod, sortBy, isDemo]);

  // Maintain search input focus after data updates
  useEffect(() => {
    if (debouncedSearchTerm && searchInputRef.current && !isSearching) {
      requestAnimationFrame(() => {
        if (
          searchInputRef.current &&
          !searchInputRef.current.matches(":focus")
        ) {
          searchInputRef.current.focus();
        }
      });
    }
  }, [casinos, isSearching, debouncedSearchTerm]);

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchCasinos();
  }, [fetchCasinos]);

  // Auto-select top 2 casinos for comparison if not chosen yet
  useEffect(() => {
    if (casinos.length >= 2 && comparisonIds.length === 0) {
      setComparisonIds([casinos[0].id, casinos[1].id]);
    }
  }, [casinos, comparisonIds.length]);

  // Fetch detailed history when a casino modal is opened
  useEffect(() => {
    if (!selectedCasino?.id) {
      setDetailedCasino(null);
      return;
    }

    let isMounted = true;
    setLoadingDetail(true);

    casinoAPI
      .getCasino(selectedCasino.id, true)
      .then((data) => {
        if (isMounted) {
          setDetailedCasino(data);
          setLoadingDetail(false);
        }
      })
      .catch((err) => {
        console.error("Error fetching casino history:", err);
        if (isMounted) {
          setDetailedCasino(selectedCasino);
          setLoadingDetail(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [selectedCasino]);

  const filteredData = useMemo(() => {
    if (!casinos || casinos.length === 0) return [];

    const enriched = casinos.map((casino) => {
      // Calculate Slot Hold & RTP
      let slotHold = Number(casino.totalSlotWinPercent) || 0;
      if (slotHold === 0 && casino.totalSlotHandle > 0 && casino.totalSlotWin > 0) {
        slotHold = Number(((casino.totalSlotWin / casino.totalSlotHandle) * 100).toFixed(1));
      }
      if (slotHold > 30) slotHold = 9.1;

      const slotRtp = slotHold > 0 ? Number((100 - slotHold).toFixed(1)) : 90.9;
      const slotPayout =
        casino.totalSlotHandle && casino.totalSlotWin
          ? Math.max(0, casino.totalSlotHandle - casino.totalSlotWin)
          : (casino.totalSlotWin || 0) * 10;

      // Calculate Table Hold & RTP
      let tableHold = Number(casino.totalTableWinPercent) || 0;
      if (tableHold === 0 && casino.totalTableDrop > 0 && casino.totalTableWin > 0) {
        tableHold = Number(((casino.totalTableWin / casino.totalTableDrop) * 100).toFixed(1));
      }
      if (tableHold > 45) tableHold = 19.5;

      const tableRtp = tableHold > 0 ? Number((100 - tableHold).toFixed(1)) : 80.5;
      const tablePayout =
        casino.totalTableDrop && casino.totalTableWin
          ? Math.max(0, casino.totalTableDrop - casino.totalTableWin)
          : (casino.totalTableWin || 0) * 4;

      return {
        ...casino,
        slotHold,
        slotRtp,
        slotPayout,
        tableHold,
        tableRtp,
        tablePayout,
        totalRevenue:
          casino.totalRevenue ||
          (casino.totalSlotWin || 0) + (casino.totalTableWin || 0),
        yearlyData: casino.yearlyData || [],
      };
    });

    // Mark the casino with the highest slot RTP ("Loosest Slots")
    let maxRtp = 0;
    enriched.forEach((c) => {
      if (c.slotRtp > maxRtp) maxRtp = c.slotRtp;
    });

    enriched.forEach((c) => {
      c.isLoosestSlots = c.slotRtp > 0 && Math.abs(c.slotRtp - maxRtp) < 0.05;
    });

    // Sort according to current sortBy
    return [...enriched].sort((a, b) => {
      if (sortBy === "highestRtp") return b.slotRtp - a.slotRtp;
      if (sortBy === "tableRtp") return b.tableRtp - a.tableRtp;
      if (sortBy === "playerPayouts") return b.slotPayout - a.slotPayout;
      if (sortBy === "totalRevenue") return (b.totalRevenue || 0) - (a.totalRevenue || 0);
      if (sortBy === "totalSlotWinPercent") return (b.slotHold || 0) - (a.slotHold || 0);
      if (sortBy === "rating") return (b.rating || 0) - (a.rating || 0);
      if (sortBy === "established") return (b.established || 0) - (a.established || 0);
      return (b.totalRevenue || 0) - (a.totalRevenue || 0);
    });
  }, [casinos, sortBy]);

  const marketSummary = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return null;
    let totalRevenue = 0;
    let totalSlotHandle = 0;
    let totalSlotWin = 0;
    let totalSlotPayout = 0;
    let totalTableDrop = 0;
    let totalTableWin = 0;
    let totalTablePayout = 0;

    filteredData.forEach((c) => {
      totalRevenue += Number(c.totalRevenue) || 0;
      totalSlotHandle += Number(c.totalSlotHandle) || 0;
      totalSlotWin += Number(c.totalSlotWin) || 0;
      totalSlotPayout += Number(c.slotPayout) || 0;
      totalTableDrop += Number(c.totalTableDrop) || 0;
      totalTableWin += Number(c.totalTableWin) || 0;
      totalTablePayout += Number(c.tablePayout) || 0;
    });

    const avgSlotRtp =
      totalSlotHandle > 0 && totalSlotPayout > 0
        ? ((totalSlotPayout / totalSlotHandle) * 100).toFixed(1)
        : "90.9";
    const avgSlotHold =
      totalSlotHandle > 0 && totalSlotWin > 0
        ? ((totalSlotWin / totalSlotHandle) * 100).toFixed(1)
        : "9.1";

    return {
      totalRevenue,
      totalSlotHandle,
      totalSlotWin,
      totalSlotPayout,
      totalTableDrop,
      totalTableWin,
      totalTablePayout,
      avgSlotRtp,
      avgSlotHold,
    };
  }, [filteredData]);

  const getYearOverYearChange = (casino) => {
    if (
      casino.yoyChangePercent !== null &&
      casino.yoyChangePercent !== undefined &&
      !isNaN(casino.yoyChangePercent)
    ) {
      return parseFloat(casino.yoyChangePercent);
    }

    if (!casino.yearlyData || casino.yearlyData.length < 2) return 0;

    const currentYear = casino.yearlyData.find((y) => y.year === 2024);
    const previousYear = casino.yearlyData.find((y) => y.year === 2023);

    if (!currentYear || !previousYear || previousYear.revenue === 0) return 0;

    return (
      ((currentYear.revenue - previousYear.revenue) / previousYear.revenue) * 100
    );
  };

  const toggleCompareCasino = (casinoId) => {
    setComparisonIds((prev) => {
      if (prev.includes(casinoId)) {
        if (prev.length === 1) return prev;
        return prev.filter((id) => id !== casinoId);
      }
      if (prev.length >= 3) {
        return [prev[1], prev[2], casinoId];
      }
      return [...prev, casinoId];
    });
  };

  const selectedLocationObj = useMemo(() => {
    return (
      locations.find((l) => l.value === selectedLocation) || {
        value: selectedLocation,
        label: selectedLocation,
        status: "active",
      }
    );
  }, [selectedLocation]);

  const comparisonCasinos = useMemo(() => {
    return filteredData.filter((c) => comparisonIds.includes(c.id));
  }, [filteredData, comparisonIds]);

  const comparisonChartData = useMemo(() => {
    return comparisonCasinos.map((c) => {
      const cleanName = (c.casino || "")
        .replace(" Hotel Casino & Spa", "")
        .replace(" Resort Atlantic City", "")
        .replace(" Atlantic City", "");

      if (perspective === "player") {
        return {
          name: cleanName,
          "Paid to Players": Number((((c.slotPayout || 0) + (c.tablePayout || 0)) / 1000000).toFixed(1)),
          "Slots Won by Players": Number(((c.slotPayout || 0) / 1000000).toFixed(1)),
          "Tables Won by Players": Number(((c.tablePayout || 0) / 1000000).toFixed(1)),
        };
      }

      return {
        name: cleanName,
        Revenue: Number(((c.totalRevenue || 0) / 1000000).toFixed(1)),
        Slots: Number(((c.totalSlotWin || 0) / 1000000).toFixed(1)),
        Tables: Number(((c.totalTableWin || 0) / 1000000).toFixed(1)),
      };
    });
  }, [comparisonCasinos, perspective]);

  const CasinoDetailModal = ({ casino, onClose }) => {
    // Lock background body scroll while modal is active
    useEffect(() => {
      const originalOverflow = document.body.style.overflow;
      const originalOverscroll = document.body.style.overscrollBehavior;
      document.body.style.overflow = "hidden";
      document.body.style.overscrollBehavior = "none";
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.overscrollBehavior = originalOverscroll;
      };
    }, []);

    if (!casino) return null;

    const activeCasino = detailedCasino || casino;
    const slotHold = Number(activeCasino.totalSlotWinPercent) || (activeCasino.totalSlotHandle > 0 ? Number(((activeCasino.totalSlotWin / activeCasino.totalSlotHandle) * 100).toFixed(1)) : 9.1);
    const slotRtp = slotHold > 0 && slotHold <= 30 ? (100 - slotHold).toFixed(1) : "90.9";
    const slotPayout = (activeCasino.totalSlotHandle && activeCasino.totalSlotWin)
      ? Math.max(0, activeCasino.totalSlotHandle - activeCasino.totalSlotWin)
      : (activeCasino.totalSlotWin || 0) * 10;

    const tableHold = Number(activeCasino.totalTableWinPercent) || (activeCasino.totalTableDrop > 0 ? Number(((activeCasino.totalTableWin / activeCasino.totalTableDrop) * 100).toFixed(1)) : 18.5);
    const tableRtp = tableHold > 0 && tableHold <= 50 ? (100 - tableHold).toFixed(1) : "81.5";
    const tablePayout = (activeCasino.totalTableDrop && activeCasino.totalTableWin)
      ? Math.max(0, activeCasino.totalTableDrop - activeCasino.totalTableWin)
      : (activeCasino.totalTableWin || 0) * 4;

    // Chronologically sorted (oldest to newest) multi-year revenue trend data
    const trendData = (() => {
      let raw = [];
      if (activeCasino.yearlyData && activeCasino.yearlyData.length > 0) {
        raw = activeCasino.yearlyData;
      } else if (activeCasino.monthlyData && activeCasino.monthlyData.length > 0) {
        const yearMap = {};
        activeCasino.monthlyData.forEach((m) => {
          if (!yearMap[m.year]) {
            yearMap[m.year] = { year: m.year, revenue: 0, slotWin: 0, tableWin: 0 };
          }
          yearMap[m.year].revenue += (Number(m.totalRevenue) || 0);
          yearMap[m.year].slotWin += (Number(m.slotWin) || 0);
          yearMap[m.year].tableWin += (Number(m.tableWin) || 0);
        });
        raw = Object.values(yearMap);
      }

      const sorted = [...raw].sort((a, b) => Number(a.year) - Number(b.year));

      return sorted.map((y, idx, arr) => {
        const rev = Number(((y.revenue || 0) / 1000000).toFixed(1));
        const slots = Number(((y.slotWin || 0) / 1000000).toFixed(1));
        const tables = Number(((y.tableWin || 0) / 1000000).toFixed(1));
        const prevRev = idx > 0 ? Number(((arr[idx - 1].revenue || 0) / 1000000).toFixed(1)) : null;
        const changePct = prevRev && prevRev > 0 ? Number((((rev - prevRev) / prevRev) * 100).toFixed(1)) : null;

        return {
          year: String(y.year),
          revenue: rev,
          slots,
          tables,
          changePct,
        };
      });
    })();

    const yoy = getYearOverYearChange(activeCasino);

    return (
      <div
        className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50 animate-fadeIn overscroll-contain"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="bg-gray-900 border border-gray-800 rounded-2xl sm:rounded-3xl max-w-4xl w-full max-h-[92vh] sm:max-h-[90vh] flex flex-col shadow-2xl overflow-hidden overscroll-contain"
        >
          {/* Sticky Header - Always visible on mobile */}
          <div className="p-4 sm:p-6 border-b border-gray-800 bg-gray-950/80 flex justify-between items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <h2 className="text-lg sm:text-2xl font-bold text-white truncate">
                  {activeCasino.casino}
                </h2>
                <span className={`px-2.5 py-0.5 border text-[11px] sm:text-xs font-semibold rounded-full whitespace-nowrap ${
                  perspective === "player"
                    ? "bg-emerald-600/20 border-emerald-500/40 text-emerald-300"
                    : "bg-purple-600/30 border-purple-500/40 text-purple-300"
                }`}>
                  {perspective === "player"
                    ? (activeCasino.isLoosestSlots ? "🏆 Loosest Slots Property" : "Verified Player Odds")
                    : (activeCasino.latestDataYear ? `${activeCasino.latestDataYear} Financials` : "Live Report")}
                </span>
              </div>
              <div className="flex flex-wrap items-center text-gray-200 gap-x-3 gap-y-1 text-xs sm:text-sm">
                <span className="flex items-center">
                  <MapPin className="h-3.5 w-3.5 mr-1 text-purple-400 shrink-0" />
                  {activeCasino.city}, {activeCasino.state} ({activeCasino.location || "Boardwalk"})
                </span>
                <span>Est. {activeCasino.established || "N/A"}</span>
                <div className="flex items-center">
                  <Star className="h-3.5 w-3.5 text-yellow-500 mr-1 fill-yellow-500 shrink-0" />
                  <span className="text-yellow-400 font-medium">
                    {activeCasino.rating || "N/A"}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-300 hover:text-white bg-gray-800/80 hover:bg-gray-700 rounded-xl transition-colors shrink-0"
              aria-label="Close details"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {/* Scrollable Content Body */}
          <div
            className="flex-1 overflow-y-auto p-4 sm:p-7 space-y-6 sm:space-y-8 overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {/* 3 Main Highlight Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
              {perspective === "player" ? (
                <>
                  <div className="bg-gray-800/60 border border-emerald-500/30 rounded-xl sm:rounded-2xl p-4 sm:p-5 shadow-lg shadow-emerald-950/20">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-200 mb-1">
                      Slot Payback Rate (RTP)
                    </h3>
                    <p className="text-2xl sm:text-3xl font-extrabold text-emerald-400">
                      {slotRtp}%
                    </p>
                    <p className="text-emerald-300/90 text-[11px] sm:text-xs mt-1.5 font-medium">
                      Returns ${slotRtp} per $100 wagered
                    </p>
                  </div>

                  <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl sm:rounded-2xl p-4 sm:p-5">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-200 mb-1">
                      Total Paid to Players
                    </h3>
                    <p className="text-2xl sm:text-3xl font-bold text-blue-400">
                      {formatCurrency(slotPayout + tablePayout)}
                    </p>
                    <p className="text-gray-300 text-[11px] sm:text-xs mt-1.5 font-medium">
                      Combined Slots & Tables Prizes
                    </p>
                  </div>

                  <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl sm:rounded-2xl p-4 sm:p-5">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-200 mb-1">
                      Table Player Return
                    </h3>
                    <p className="text-2xl sm:text-3xl font-bold text-amber-400">
                      {tableRtp}%
                    </p>
                    <p className="text-gray-300 text-[11px] sm:text-xs mt-1.5 font-medium">
                      {tableHold}% casino house hold
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl sm:rounded-2xl p-4 sm:p-5">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-200 mb-1">
                      Total Gaming Revenue
                    </h3>
                    <p className="text-2xl sm:text-3xl font-bold text-green-400">
                      {formatCurrency(activeCasino.totalRevenue)}
                    </p>
                    <div className="flex items-center mt-1.5">
                      {yoy >= 0 ? (
                        <ArrowUpRight className="h-4 w-4 text-green-400 mr-1" />
                      ) : (
                        <ArrowDownRight className="h-4 w-4 text-red-400 mr-1" />
                      )}
                      <span
                        className={
                          yoy >= 0
                            ? "text-green-400 text-xs sm:text-sm font-semibold"
                            : "text-red-400 text-xs sm:text-sm font-semibold"
                        }
                      >
                        {Math.abs(yoy).toFixed(1)}% vs Prior Year
                      </span>
                    </div>
                  </div>

                  <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl sm:rounded-2xl p-4 sm:p-5">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-200 mb-1">
                      Gaming Capacity
                    </h3>
                    <p className="text-2xl sm:text-3xl font-bold text-blue-400">
                      {formatNumber(activeCasino.gameCount || 0)}
                    </p>
                    <p className="text-gray-300 text-[11px] sm:text-xs mt-1.5 font-medium">
                      Active Slots & Table Units
                    </p>
                  </div>

                  <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl sm:rounded-2xl p-4 sm:p-5">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-200 mb-1">
                      Slot Hold Percentage
                    </h3>
                    <p className="text-2xl sm:text-3xl font-bold text-purple-400">
                      {activeCasino.totalSlotWinPercent || 0}%
                    </p>
                    <p className="text-gray-300 text-[11px] sm:text-xs mt-1.5 font-medium">
                      Casino Retention Rate
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Rebuilt Multi-Year Revenue Bar Chart */}
            <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl sm:rounded-2xl p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-4">
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-white">
                    {perspective === "player"
                      ? "Multi-Year Gaming Revenue & Market Size"
                      : "Multi-Year Revenue Performance"}
                  </h3>
                  <p className="text-xs text-gray-400">
                    Annual reported gross gaming win ($ Millions)
                  </p>
                </div>
                {loadingDetail && (
                  <span className="text-xs text-purple-400 flex items-center gap-1.5 animate-pulse mt-1 sm:mt-0">
                    <span className="inline-block w-2 h-2 rounded-full bg-purple-400"></span>
                    Loading history...
                  </span>
                )}
              </div>

              {trendData.length > 0 ? (
                <>
                  <div className="w-full h-56 sm:h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={trendData}
                        margin={{ top: 15, right: 10, left: -20, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                        <XAxis
                          dataKey="year"
                          stroke="#9CA3AF"
                          tick={{ fill: "#9CA3AF", fontSize: 12 }}
                          axisLine={{ stroke: "#4B5563" }}
                          tickLine={false}
                        />
                        <YAxis
                          stroke="#9CA3AF"
                          tickFormatter={(val) => `$${val}M`}
                          tick={{ fill: "#9CA3AF", fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#111827",
                            border: "1px solid #374151",
                            borderRadius: "12px",
                            padding: "8px 12px",
                          }}
                          labelStyle={{ color: "#F9FAFB", fontWeight: "bold" }}
                          formatter={(value) => [
                            `$${value}M`,
                            perspective === "player" ? "Casino Win" : "Total Revenue",
                          ]}
                        />
                        <Bar
                          dataKey="revenue"
                          fill={perspective === "player" ? "#10B981" : "#8B5CF6"}
                          radius={[6, 6, 0, 0]}
                          maxBarSize={48}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Year-by-Year Quick Glance Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 pt-3 border-t border-gray-700/50">
                    {trendData.map((d) => (
                      <div
                        key={d.year}
                        className="p-2 sm:p-2.5 bg-gray-900/60 rounded-xl border border-gray-700/50 text-center"
                      >
                        <span className="text-[11px] sm:text-xs text-gray-400 font-semibold block">
                          FY {d.year}
                        </span>
                        <span className="text-xs sm:text-sm font-bold text-white block mt-0.5">
                          ${d.revenue}M
                        </span>
                        {d.changePct !== null ? (
                          <span
                            className={`text-[10px] sm:text-[11px] font-semibold block mt-0.5 ${
                              d.changePct >= 0 ? "text-emerald-400" : "text-rose-400"
                            }`}
                          >
                            {d.changePct >= 0 ? `+${d.changePct}%` : `${d.changePct}%`}
                          </span>
                        ) : (
                          <span className="text-[10px] text-gray-500 block mt-0.5">Base Year</span>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="py-10 text-center text-gray-400 bg-gray-900/40 rounded-xl border border-dashed border-gray-700">
                  <p className="text-xs sm:text-sm">
                    {loadingDetail
                      ? "Retrieving multi-year records..."
                      : "Historical multi-year breakdown will display as more annual reports are processed."}
                  </p>
                </div>
              )}
            </div>

            {/* Performance breakdown cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {perspective === "player" ? (
                <>
                  <div className="bg-gray-800/60 border border-emerald-500/30 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                    <div className="flex items-center gap-2 mb-3 sm:mb-4">
                      <Coins className="h-5 w-5 text-emerald-400" />
                      <h3 className="text-base sm:text-lg font-semibold text-white">
                        Slot Machine Odds & Paybacks
                      </h3>
                    </div>
                    <div className="space-y-2.5 sm:space-y-3">
                      <div className="flex justify-between py-1 border-b border-gray-700/40 text-xs sm:text-sm">
                        <span className="text-gray-200 font-medium">Return to Player (RTP):</span>
                        <span className="text-emerald-400 font-bold">{slotRtp}%</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-700/40 text-xs sm:text-sm">
                        <span className="text-gray-200 font-medium">Prizes Won by Players:</span>
                        <span className="text-white font-semibold">{formatCurrency(slotPayout)}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-700/40 text-xs sm:text-sm">
                        <span className="text-gray-200 font-medium">Total Wagered by Players:</span>
                        <span className="text-white font-semibold">{formatCurrency(activeCasino.totalSlotHandle || 0)}</span>
                      </div>
                      <div className="flex justify-between py-1 text-xs sm:text-sm">
                        <span className="text-gray-200 font-medium">Casino Hold (House Margin):</span>
                        <span className="text-gray-200 font-medium">{slotHold}%</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-800/60 border border-amber-500/30 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                    <div className="flex items-center gap-2 mb-3 sm:mb-4">
                      <Dices className="h-5 w-5 text-amber-400" />
                      <h3 className="text-base sm:text-lg font-semibold text-white">
                        Table Games Odds & Retention
                      </h3>
                    </div>
                    <div className="space-y-2.5 sm:space-y-3">
                      <div className="flex justify-between py-1 border-b border-gray-700/40 text-xs sm:text-sm">
                        <span className="text-gray-200 font-medium">Player Return Rate:</span>
                        <span className="text-amber-400 font-bold">{tableRtp}%</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-700/40 text-xs sm:text-sm">
                        <span className="text-gray-200 font-medium">Chips Cashed by Players:</span>
                        <span className="text-white font-semibold">{formatCurrency(tablePayout)}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-700/40 text-xs sm:text-sm">
                        <span className="text-gray-200 font-medium">Chips Purchased (Drop):</span>
                        <span className="text-white font-semibold">{formatCurrency(activeCasino.totalTableDrop || 0)}</span>
                      </div>
                      <div className="flex justify-between py-1 text-xs sm:text-sm">
                        <span className="text-gray-200 font-medium">House Hold (Retention):</span>
                        <span className="text-gray-200 font-medium">{tableHold}%</span>
                      </div>
                    </div>
                  </div>

                  <div className="col-span-full p-3.5 sm:p-4 rounded-xl sm:rounded-2xl bg-emerald-950/30 border border-emerald-500/30 flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
                    <div className="text-xs text-gray-200 leading-relaxed font-normal">
                      <strong className="text-emerald-300 font-semibold">How Player RTP Works:</strong> A{" "}
                      <strong>{slotRtp}% Return to Player (RTP)</strong> means that across millions of spins, this property returns{" "}
                      <strong>${slotRtp} in payouts for every $100 played</strong>. These figures are calculated directly from official monthly filings submitted to the New Jersey Division of Gaming Enforcement (DGE).
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                    <div className="flex items-center gap-2 mb-3 sm:mb-4">
                      <Coins className="h-5 w-5 text-blue-400" />
                      <h3 className="text-base sm:text-lg font-semibold text-white">
                        Slot Machine Performance
                      </h3>
                    </div>
                    <div className="space-y-2.5 sm:space-y-3">
                      <div className="flex justify-between py-1 border-b border-gray-700/40 text-xs sm:text-sm">
                        <span className="text-gray-200 font-medium">Total Slot Win:</span>
                        <span className="text-white font-semibold">{formatCurrency(activeCasino.totalSlotWin || 0)}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-700/40 text-xs sm:text-sm">
                        <span className="text-gray-200 font-medium">Total Handle (Wagered):</span>
                        <span className="text-white font-semibold">{formatCurrency(activeCasino.totalSlotHandle || 0)}</span>
                      </div>
                      <div className="flex justify-between py-1 text-xs sm:text-sm">
                        <span className="text-gray-200 font-medium">Win Percentage (Hold):</span>
                        <span className="text-green-400 font-semibold">{activeCasino.totalSlotWinPercent || 0}%</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                    <div className="flex items-center gap-2 mb-3 sm:mb-4">
                      <Dices className="h-5 w-5 text-orange-400" />
                      <h3 className="text-base sm:text-lg font-semibold text-white">
                        Table Games Performance
                      </h3>
                    </div>
                    <div className="space-y-2.5 sm:space-y-3">
                      <div className="flex justify-between py-1 border-b border-gray-700/40 text-xs sm:text-sm">
                        <span className="text-gray-200 font-medium">Total Table Win:</span>
                        <span className="text-white font-semibold">{formatCurrency(activeCasino.totalTableWin || 0)}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-700/40 text-xs sm:text-sm">
                        <span className="text-gray-200 font-medium">Total Drop (Chips Purchased):</span>
                        <span className="text-white font-semibold">{formatCurrency(activeCasino.totalTableDrop || 0)}</span>
                      </div>
                      <div className="flex justify-between py-1 text-xs sm:text-sm">
                        <span className="text-gray-200 font-medium">Table Win Percentage:</span>
                        <span className="text-green-400 font-semibold">{activeCasino.totalTableWinPercent || 0}%</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-500 mx-auto"></div>
          <p className="text-white text-xl mt-4">Loading casino data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-4">
            Error Loading Data
          </h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={fetchCasinos}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white w-full overflow-x-hidden">
      {isDemo && (
        <div className="bg-gradient-to-r from-purple-900/90 via-indigo-900/90 to-blue-900/90 border-b border-purple-500/40 text-white px-4 py-2.5 text-center text-xs sm:text-sm font-medium flex flex-wrap items-center justify-center gap-3 sticky top-0 z-50 backdrop-blur-md shadow-lg">
          <span className="flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-purple-300" />
            <span>You are viewing <strong>Demo Mode (Sample Data)</strong>.</span>
          </span>
          <button
            onClick={onSignUp}
            className="px-3.5 py-1 bg-white text-gray-950 font-bold rounded-lg hover:bg-purple-100 transition-all text-xs shadow-md"
          >
            Unlock All 500+ Casinos & Exports →
          </button>
        </div>
      )}

      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/20 to-blue-900/20 pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Top navigation */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={onBackToLanding}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-800/60 backdrop-blur-sm border border-gray-700/80 rounded-xl text-gray-200 hover:text-white hover:border-gray-600 transition-all text-sm"
            >
              <Home className="h-4 w-4" />
              Back to Home
            </button>

            <div className="flex items-center gap-2.5">
              {isAdmin && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-semibold rounded-full">
                  <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
                  Admin
                </span>
              )}
              {user ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setProfileModalOpen(true)}
                    className="inline-flex items-center gap-2 text-xs text-gray-200 hover:text-white bg-gray-800/80 hover:bg-gray-800 border border-gray-700/60 hover:border-gray-600 px-3.5 py-1.5 rounded-xl transition-all group"
                    title="Account Settings"
                  >
                    <User className="h-3.5 w-3.5 text-purple-400 group-hover:text-purple-300" />
                    <span>{user.user_metadata?.full_name || user.email}</span>
                    <Settings className="h-3 w-3 text-gray-500 group-hover:text-gray-300 ml-0.5" />
                  </button>
                  <button
                    onClick={() => {
                      signOut();
                      onBackToLanding();
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800/60 hover:bg-gray-800 border border-gray-700/80 hover:border-gray-600 rounded-xl text-xs text-gray-200 hover:text-white transition-all"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                    Sign Out
                  </button>
                </div>
              ) : (
                <button
                  onClick={onSignUp}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-purple-500/25 transition-all"
                >
                  Join / Sign In
                </button>
              )}
            </div>
          </div>



          <div className="text-center mb-10">
            <h1 className="text-4xl sm:text-6xl font-extrabold text-purple-300 sm:text-transparent sm:bg-clip-text sm:bg-gradient-to-r sm:from-purple-300 sm:via-blue-300 sm:to-indigo-200 mb-4">
              Casino Figures
            </h1>
            <p className="text-lg text-gray-200 max-w-2xl mx-auto">
              {perspective === "player"
                ? "Official state regulatory payout percentages, slot return-to-player (RTP) rates, and casino odds verified monthly."
                : "Real-time gaming win, handle, and hold statistics extracted from official state gaming regulatory reports."}
            </p>
          </div>

          {/* Search bar */}
          <div className="max-w-3xl mx-auto mb-8 px-1">
            <div className="relative">
              <Search className="absolute left-4 sm:left-5 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 sm:h-5 sm:w-5" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={
                  perspective === "player"
                    ? "Search casinos (e.g. Borgata, Hard Rock)..."
                    : "Search casinos (e.g. Borgata, Marina)..."
                }
                className="w-full pl-11 sm:pl-14 pr-10 sm:pr-14 py-3 sm:py-4 bg-gray-800/60 backdrop-blur-md border border-gray-700/80 rounded-xl sm:rounded-2xl text-white placeholder-gray-400 text-sm sm:text-base focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all outline-none truncate"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                spellCheck="false"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 sm:right-5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white p-1"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              {isSearching && (
                <div className="absolute right-3 sm:right-5 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-24">
        {/* Controls Bar */}
        <div className="bg-gray-800/40 backdrop-blur-md border border-gray-800 rounded-2xl p-3.5 sm:p-4 mt-2 sm:mt-4 mb-6 sm:mb-8 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 sm:gap-4">
          <div className="grid grid-cols-1 sm:flex sm:flex-wrap items-center gap-2.5 sm:gap-3 w-full md:w-auto">
            {/* Location selector */}
            <div className="w-full sm:w-auto">
              <label className="block sm:hidden text-[11px] font-semibold text-gray-400 mb-1 ml-0.5">
                Jurisdiction / Location
              </label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="w-full sm:w-auto px-3.5 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-xs sm:text-sm font-medium text-white focus:ring-2 focus:ring-purple-500 outline-none cursor-pointer"
              >
                {locations.map((loc) => (
                  <option key={loc.value} value={loc.value}>
                    {loc.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Time Period selector */}
            <div className="w-full sm:w-auto">
              <label className="block sm:hidden text-[11px] font-semibold text-gray-400 mb-1 ml-0.5">
                Reporting Period
              </label>
              <select
                value={selectedTimePeriod}
                onChange={(e) => setSelectedTimePeriod(e.target.value)}
                className="w-full sm:w-auto px-3.5 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-xs sm:text-sm font-medium text-white focus:ring-2 focus:ring-purple-500 outline-none cursor-pointer"
              >
                {availablePeriods.months && availablePeriods.months.length > 0 && (
                  <optgroup label="Monthly Reports (Latest First)">
                    {availablePeriods.months.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label} {m.value === "2026-08" ? " • Latest" : ""}
                      </option>
                    ))}
                  </optgroup>
                )}
                <optgroup label="Fiscal Years (Annual)">
                  {(availablePeriods.years || ["2026", "2025", "2024", "2023", "2022"]).map((year) => (
                    <option key={year} value={year}>
                      Fiscal Year {year} {year === "2026" ? " (YTD)" : ""}
                    </option>
                  ))}
                  <option value="All Years">All Years (Aggregated)</option>
                </optgroup>
              </select>
            </div>

            {/* Sort By selector */}
            <div className="w-full sm:w-auto">
              <label className="block sm:hidden text-[11px] font-semibold text-gray-400 mb-1 ml-0.5">
                Sort Casinos By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full sm:w-auto px-3.5 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-xs sm:text-sm font-medium text-white focus:ring-2 focus:ring-purple-500 outline-none cursor-pointer"
              >
                {perspective === "player" ? (
                  <>
                    <option value="highestRtp">Sort by Slot Payback (Loosest Slots)</option>
                    <option value="tableRtp">Sort by Best Table Return %</option>
                    <option value="playerPayouts">Sort by Total Paid to Players</option>
                    <option value="rating">Sort by Casino Rating</option>
                    <option value="established">Sort by Established Date</option>
                  </>
                ) : (
                  <>
                    <option value="totalRevenue">Sort by Total Revenue</option>
                    <option value="rating">Sort by Rating</option>
                    <option value="totalSlotWinPercent">Sort by Slot Hold %</option>
                    <option value="established">Sort by Established Date</option>
                  </>
                )}
              </select>
            </div>

            {(selectedLocation !== "All Locations" ||
              searchTerm !== "" ||
              selectedTimePeriod !== (availablePeriods.latestMonth?.value || "2026-08") ||
              sortBy !== (perspective === "player" ? "highestRtp" : "totalRevenue")) && (
              <button
                onClick={() => {
                  setSelectedLocation("All Locations");
                  setSelectedTimePeriod(availablePeriods.latestMonth?.value || "2026-08");
                  setSearchTerm("");
                  setSortBy(perspective === "player" ? "highestRtp" : "totalRevenue");
                }}
                className="flex items-center justify-center gap-1.5 px-3.5 py-2.5 text-xs text-gray-400 hover:text-white bg-gray-800/80 hover:bg-gray-700 rounded-xl transition-colors w-full sm:w-auto mt-1 sm:mt-0"
                title="Reset all filters"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset Filters
              </button>
            )}
          </div>

          {/* View Mode Toggle Buttons */}
          <div className="w-full sm:w-auto grid grid-cols-3 sm:flex items-center bg-gray-900/90 p-1 border border-gray-700/80 rounded-xl shadow-inner mt-1 md:mt-0">
            <button
              onClick={() => setViewMode("cards")}
              className={`flex items-center justify-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                viewMode === "cards"
                  ? "bg-purple-600 text-white shadow-md shadow-purple-600/30"
                  : "text-gray-400 hover:text-white hover:bg-gray-800"
              }`}
            >
              <LayoutGrid className="h-4 w-4" />
              <span>Cards</span>
            </button>

            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center justify-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                viewMode === "list"
                  ? "bg-purple-600 text-white shadow-md shadow-purple-600/30"
                  : "text-gray-400 hover:text-white hover:bg-gray-800"
              }`}
            >
              <List className="h-4 w-4" />
              <span>Table</span>
            </button>

            <button
              onClick={() => setViewMode("compare")}
              className={`flex items-center justify-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                viewMode === "compare"
                  ? "bg-purple-600 text-white shadow-md shadow-purple-600/30"
                  : "text-gray-400 hover:text-white hover:bg-gray-800"
              }`}
            >
              <Scale className="h-4 w-4" />
              <span>Compare</span>
              {comparisonIds.length > 0 && (
                <span className="ml-1 px-1.5 py-0.2 bg-purple-900 text-purple-200 text-xs rounded-full">
                  {comparisonIds.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Header Results Counter & Perspective Switcher */}
        {selectedLocationObj.status === "active" && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">
            <div className="flex items-center justify-center sm:justify-start gap-2.5 flex-wrap text-center sm:text-left">
              <h2 className="text-lg sm:text-xl font-bold text-white">
                {filteredData.length} Casino
                {filteredData.length !== 1 ? "s" : ""} Found
              </h2>
              {selectedTimePeriod !== "All Years" && (
                <span className="px-2.5 py-0.5 bg-purple-500/20 border border-purple-500/30 text-purple-300 text-xs rounded-md">
                  Showing {getPeriodLabel(selectedTimePeriod)} Figures
                </span>
              )}
              {perspective === "player" && marketSummary && (
                <span className="px-2.5 py-0.5 bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-xs rounded-md flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  Market Avg Slot Payback: <strong>{marketSummary.avgSlotRtp}%</strong>
                </span>
              )}
              {perspective === "business" && marketSummary && (
                <span className="px-2.5 py-0.5 bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs rounded-md">
                  Total Market GGR: <strong>{formatCurrency(marketSummary.totalRevenue)}</strong>
                </span>
              )}
            </div>

            {/* Perspective Switcher - Center aligned on mobile */}
            <div className="w-full sm:w-auto flex justify-center">
              <div className="flex items-center w-full sm:w-auto bg-gray-900/90 p-1 border border-gray-700/80 rounded-xl shadow-inner">
                <button
                  type="button"
                  onClick={() => {
                    setPerspective("player");
                    if (sortBy === "totalRevenue" || sortBy === "totalSlotWinPercent") {
                      setSortBy("highestRtp");
                    }
                  }}
                  className={`flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3 sm:px-3.5 py-2 sm:py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    perspective === "player"
                      ? "bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-md shadow-emerald-900/40"
                      : "text-gray-400 hover:text-white"
                  }`}
                  title="Player Odds: Invert hold to Return to Player (RTP %)"
                >
                  <Coins className="h-3.5 w-3.5 text-emerald-300 shrink-0" />
                  <span>Player Odds (RTP %)</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPerspective("business");
                    if (sortBy === "highestRtp" || sortBy === "tableRtp" || sortBy === "playerPayouts") {
                      setSortBy("totalRevenue");
                    }
                  }}
                  className={`flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3 sm:px-3.5 py-2 sm:py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    perspective === "business"
                      ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md shadow-purple-900/40"
                      : "text-gray-400 hover:text-white"
                  }`}
                  title="Casino Hold: Traditional gross gaming revenue win %"
                >
                  <Briefcase className="h-3.5 w-3.5 text-purple-300 shrink-0" />
                  <span>Casino Hold %</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Coming Soon state if user selected an upcoming location */}
        {selectedLocationObj.status === "upcoming" ? (
          <div className="max-w-2xl mx-auto my-12 text-center p-10 bg-gray-800/40 backdrop-blur-md rounded-3xl border border-purple-500/30 shadow-2xl animate-fadeIn">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
              <Sparkles className="h-8 w-8" />
            </div>
            <span className="px-3.5 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-xs font-semibold uppercase tracking-wider text-purple-300">
              Jurisdiction Ingestion In Progress
            </span>
            <h3 className="text-3xl font-bold text-white mt-4 mb-2">
              {selectedLocation}
            </h3>
            <p className="text-gray-300 mb-6 text-sm leading-relaxed max-w-lg mx-auto">
              State gaming regulatory tax returns and property metrics for{" "}
              <strong>{selectedLocation}</strong> are being configured into our
              multi-jurisdiction extractor. In the meantime, explore live gaming
              data from Atlantic City, NJ.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button
                onClick={() => setSelectedLocation("Atlantic City, NJ")}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold rounded-xl transition-all shadow-lg shadow-purple-600/30"
              >
                Switch to Atlantic City, NJ (Live)
              </button>
              <button
                onClick={() => setSelectedLocation("All Locations")}
                className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white text-sm rounded-xl border border-gray-700 transition-all"
              >
                View All Live Casinos
              </button>
            </div>
          </div>
        ) : filteredData.length === 0 ? (
          /* Empty search state */
          <div className="text-center py-16 bg-gray-800/30 rounded-3xl border border-gray-800 max-w-lg mx-auto my-8">
            <div className="text-5xl mb-4">🎰</div>
            <h3 className="text-2xl font-bold text-white mb-2">
              No Casinos Match Your Search
            </h3>
            <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
              We couldn't find any results matching "{searchTerm}". Try
              adjusting your search keywords or clearing your filters.
            </p>
            <button
              onClick={() => {
                setSearchTerm("");
                setSelectedLocation("All Locations");
              }}
              className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-xl transition-colors"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          /* Render Active View Mode */
          <>
            {/* VIEW MODE 1: CARDS */}
            {viewMode === "cards" && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredData.map((casino, index) => (
                  <div
                    key={casino.id || casino.casino}
                    className={`group rounded-2xl overflow-hidden transition-all duration-300 flex flex-col justify-between border ${
                      casino.isLocked
                        ? "bg-gray-900/40 border-gray-800 opacity-60 filter blur-[1.5px] select-none cursor-pointer"
                        : "bg-gray-800/50 backdrop-blur-sm hover:bg-gray-800/80 border-gray-700/80 hover:border-purple-500 cursor-pointer"
                    }`}
                    onClick={() => {
                      if (casino.isLocked) {
                        onSignUp();
                      } else {
                        setSelectedCasino(casino);
                      }
                    }}
                  >
                    <div className="p-7">
                      <div className="flex justify-between items-start mb-5">
                        <div className="flex-1 pr-2">
                          <h3 className="text-xl font-bold text-white mb-1 group-hover:text-purple-300 transition-colors">
                            {casino.casino}
                          </h3>
                          <div className="flex items-center text-gray-200 text-sm mb-1.5 font-medium">
                            <MapPin className="h-3.5 w-3.5 mr-1 text-purple-400" />
                            {casino.city}, {casino.state}
                          </div>
                          <div className="flex items-center gap-1 text-sm">
                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                            <span className="text-yellow-400 font-semibold">
                              {casino.rating || "N/A"}
                            </span>
                            <span className="text-gray-300 text-xs ml-1">
                              • Est. {casino.established || "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-2">
                          {perspective === "player" && casino.isLoosestSlots ? (
                            <span className="px-2.5 py-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold rounded-full shadow-md shadow-emerald-500/30 flex items-center gap-1">
                              <Trophy className="h-3 w-3" />
                              Loosest Slots
                            </span>
                          ) : (
                            <span className="px-2.5 py-0.5 bg-purple-600/80 text-white text-xs font-bold rounded-full">
                              #{index + 1}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCompareCasino(casino.id);
                            }}
                            className={`p-1.5 rounded-lg border text-xs flex items-center gap-1 transition-colors ${
                              comparisonIds.includes(casino.id)
                                ? "bg-purple-600/30 border-purple-500 text-purple-300"
                                : "bg-gray-950/40 border-gray-700 text-gray-300 hover:text-white"
                            }`}
                            title="Toggle comparison"
                          >
                            <Scale className="h-3.5 w-3.5" />
                            {comparisonIds.includes(casino.id) ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <Plus className="h-3 w-3" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-4 mb-6">
                        {perspective === "player" ? (
                          <>
                            <div className="flex justify-between items-baseline pt-2">
                              <span className="text-gray-200 text-sm font-medium">
                                Slot Payback (RTP):
                              </span>
                              <div className="text-right">
                                <span className="text-2xl font-black text-emerald-400 tracking-tight">
                                  {casino.slotRtp ? `${casino.slotRtp}%` : "N/A"}
                                </span>
                                <div className="text-[11px] text-emerald-300/80 font-medium">
                                  {casino.slotRtp ? `Returns $${casino.slotRtp} per $100` : ""}
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-gray-900/60 rounded-xl p-3 border border-gray-800">
                                <div className="text-xs text-gray-200 mb-0.5 font-medium">
                                  Paid to Slot Players
                                </div>
                                <div className="text-base font-bold text-blue-400">
                                  {formatCurrency(casino.slotPayout || 0)}
                                </div>
                                <div className="text-[11px] text-gray-300 mt-0.5 font-medium">
                                  of {formatCurrency(casino.totalSlotHandle || 0)} bet
                                </div>
                              </div>
                              <div className="bg-gray-900/60 rounded-xl p-3 border border-gray-800">
                                <div className="text-xs text-gray-200 mb-0.5 font-medium">
                                  Table Player Return
                                </div>
                                <div className="text-base font-bold text-amber-400">
                                  {casino.tableRtp ? `${casino.tableRtp}%` : "N/A"}
                                </div>
                                <div className="text-[11px] text-gray-300 mt-0.5 font-medium">
                                  {casino.tableHold || 0}% house hold
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex justify-between items-baseline pt-2">
                              <span className="text-gray-200 text-sm font-medium">
                                Total Revenue:
                              </span>
                              <span className="text-2xl font-black text-green-400 tracking-tight">
                                {formatCurrency(casino.totalRevenue || 0)}
                              </span>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-gray-900/60 rounded-xl p-3 border border-gray-800">
                                <div className="text-xs text-gray-200 mb-0.5 font-medium">
                                  Slots Win
                                </div>
                                <div className="text-base font-bold text-blue-400">
                                  {formatCurrency(casino.totalSlotWin || 0)}
                                </div>
                                <div className="text-[11px] text-gray-300 mt-0.5 font-medium">
                                  {casino.slotHold || 0}% hold
                                </div>
                              </div>
                              <div className="bg-gray-900/60 rounded-xl p-3 border border-gray-800">
                                <div className="text-xs text-gray-200 mb-0.5 font-medium">
                                  Table Win
                                </div>
                                <div className="text-base font-bold text-orange-400">
                                  {formatCurrency(casino.totalTableWin || 0)}
                                </div>
                                <div className="text-[11px] text-gray-300 mt-0.5 font-medium">
                                  {casino.tableHold || 0}% hold
                                </div>
                              </div>
                            </div>
                          </>
                        )}

                        <div className="flex items-center justify-between text-xs pt-1">
                          <div className="flex items-center">
                            {selectedTimePeriod.includes("-") ? (
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {casino.momChangePercent !== null && casino.momChangePercent !== undefined && (
                                  <span
                                    className={`inline-flex items-center font-semibold ${
                                      casino.momChangePercent >= 0
                                        ? "text-green-400"
                                        : "text-red-400"
                                    }`}
                                    title="Month-over-Month vs Prior Month"
                                  >
                                    {casino.momChangePercent >= 0 ? (
                                      <ArrowUpRight className="h-3.5 w-3.5 mr-0.5" />
                                    ) : (
                                      <ArrowDownRight className="h-3.5 w-3.5 mr-0.5" />
                                    )}
                                    {casino.momChangePercent >= 0 ? "+" : ""}
                                    {casino.momChangePercent.toFixed(1)}% MoM
                                  </span>
                                )}
                                {casino.yoyChangePercent !== null && casino.yoyChangePercent !== undefined && (
                                  <span
                                    className={`text-[11px] ${
                                      casino.yoyChangePercent >= 0
                                        ? "text-green-400/80"
                                        : "text-red-400/80"
                                    }`}
                                    title="Year-over-Year vs Same Month Prior Year"
                                  >
                                    ({casino.yoyChangePercent >= 0 ? "+" : ""}
                                    {casino.yoyChangePercent.toFixed(1)}% YoY)
                                  </span>
                                )}
                              </div>
                            ) : (
                              <>
                                {getYearOverYearChange(casino) >= 0 ? (
                                  <ArrowUpRight className="h-3.5 w-3.5 text-green-400 mr-0.5" />
                                ) : (
                                  <ArrowDownRight className="h-3.5 w-3.5 text-red-400 mr-0.5" />
                                )}
                                <span
                                  className={
                                    getYearOverYearChange(casino) >= 0
                                      ? "text-green-400 font-semibold"
                                      : "text-red-400 font-semibold"
                                  }
                                >
                                  {Math.abs(getYearOverYearChange(casino)).toFixed(
                                    1
                                  )}
                                  % vs Prior Year
                                </span>
                              </>
                            )}
                          </div>
                          <span className="text-gray-200 font-medium">
                            {casino.gameCount || 0} Games
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="px-7 py-3.5 bg-gray-900/40 border-t border-gray-700/60 flex justify-between items-center text-xs">
                      <span className="text-gray-300 font-medium">
                        {casino.latestDataYear
                          ? `Report Year ${casino.latestDataYear}`
                          : "Official Data"}
                      </span>
                      <span className="text-purple-300 group-hover:text-purple-200 font-semibold transition-colors flex items-center gap-1">
                        Full Breakdown →
                      </span>
                    </div>
                  </div>
                ))}

                {isDemo && (
                  <div className="col-span-full mt-4 p-8 rounded-3xl bg-gradient-to-br from-purple-950/40 via-gray-900 to-blue-950/40 border border-purple-500/40 text-center relative overflow-hidden backdrop-blur-md shadow-2xl">
                    <div className="max-w-xl mx-auto">
                      <div className="w-12 h-12 rounded-2xl bg-purple-500/20 text-purple-300 flex items-center justify-center mx-auto mb-3 border border-purple-500/30">
                        <ShieldCheck className="h-6 w-6" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-2">
                        Unlock All 500+ Commercial Casinos
                      </h3>
                      <p className="text-sm text-gray-300 mb-6">
                        You are viewing a sample preview. Join Casino Figures to access comprehensive multi-state regulatory data, 10-year historical tables, and CSV exports.
                      </p>
                      <button
                        onClick={onSignUp}
                        className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold rounded-xl shadow-lg shadow-purple-500/25 transition-all text-sm inline-flex items-center gap-2"
                      >
                        <span>Start Free Trial / Join</span>
                        <ArrowUpRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* VIEW MODE 2: TABLE (LIST) */}
            {viewMode === "list" && (
              <div className="space-y-6">
                <div className="bg-gray-800/50 backdrop-blur-md rounded-2xl border border-gray-800 overflow-hidden shadow-xl animate-fadeIn">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-gray-700/80 bg-gray-900/80 text-gray-400 text-xs uppercase tracking-wider">
                          <th className="py-4 px-4 font-semibold text-center w-12">
                            #
                          </th>
                          <th className="py-4 px-6 font-semibold">Casino</th>
                          <th className="py-4 px-6 font-semibold text-right">
                            {perspective === "player" ? "Slot Payback (RTP)" : "Total Revenue"}
                          </th>
                          <th className="py-4 px-6 font-semibold text-right">
                            {perspective === "player" ? "Paid to Slot Players" : "Slots Win (Hold)"}
                          </th>
                          <th className="py-4 px-6 font-semibold text-right">
                            {perspective === "player" ? "Table Return (Hold)" : "Tables Win (Hold)"}
                          </th>
                          <th className="py-4 px-4 font-semibold text-center">
                            Games
                          </th>
                          <th className="py-4 px-4 font-semibold text-center">
                            Compare
                          </th>
                          <th className="py-4 px-6 font-semibold text-right">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800 text-sm">
                        {filteredData.map((casino, index) => (
                          <tr
                            key={casino.id || casino.casino}
                            className={`transition-colors group cursor-pointer ${
                              casino.isLocked
                                ? "opacity-50 filter blur-[1.5px] select-none hover:bg-gray-800/30"
                                : "hover:bg-gray-800/70"
                            }`}
                            onClick={() => {
                              if (casino.isLocked) {
                                onSignUp();
                              } else {
                                setSelectedCasino(casino);
                              }
                            }}
                          >
                          <td className="py-4 px-4 text-center font-bold text-gray-400">
                            {perspective === "player" && casino.isLoosestSlots ? (
                              <span className="text-yellow-400" title="Loosest Slots">🏆</span>
                            ) : (
                              index + 1
                            )}
                          </td>
                          <td className="py-4 px-6">
                            <div className="font-bold text-white group-hover:text-purple-300 transition-colors flex items-center gap-2">
                              <span>{casino.casino}</span>
                              {perspective === "player" && casino.isLoosestSlots && (
                                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-[10px] font-bold rounded-full border border-emerald-500/30">
                                  Loosest Slots
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 flex items-center gap-2 mt-0.5">
                              <span>
                                {casino.city}, {casino.state}
                              </span>
                              <span>•</span>
                              <span className="flex items-center text-yellow-500">
                                <Star className="h-3 w-3 fill-yellow-500 mr-0.5" />
                                {casino.rating}
                              </span>
                            </div>
                          </td>

                          {perspective === "player" ? (
                            <>
                              <td className="py-4 px-6 text-right font-bold text-emerald-400 text-base">
                                {casino.slotRtp ? `${casino.slotRtp}%` : "N/A"}
                                <div className="text-[11px] text-emerald-300/80 font-normal">
                                  {casino.slotRtp ? `Returns $${casino.slotRtp} per $100` : ""}
                                </div>
                              </td>
                              <td className="py-4 px-6 text-right font-medium text-blue-400">
                                {formatCurrency(casino.slotPayout || 0)}
                                <div className="text-[11px] text-gray-400 font-normal">
                                  of {formatCurrency(casino.totalSlotHandle || 0)} bet
                                </div>
                              </td>
                              <td className="py-4 px-6 text-right font-medium text-amber-400">
                                {casino.tableRtp ? `${casino.tableRtp}%` : "N/A"}
                                <div className="text-[11px] text-gray-400 font-normal">
                                  {casino.tableHold || 0}% house hold
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="py-4 px-6 text-right font-bold text-green-400 text-base">
                                {formatCurrency(casino.totalRevenue || 0)}
                                <div className="text-[11px] text-gray-400 font-normal">
                                  {selectedTimePeriod.includes("-") ? (
                                    <span>
                                      <span className={casino.momChangePercent >= 0 ? "text-green-400" : "text-red-400"}>
                                        {casino.momChangePercent !== null && casino.momChangePercent !== undefined ? `${casino.momChangePercent >= 0 ? "+" : ""}${casino.momChangePercent.toFixed(1)}% MoM` : ""}
                                      </span>
                                      {casino.yoyChangePercent !== null && casino.yoyChangePercent !== undefined && (
                                        <span className="text-gray-400 ml-1">
                                          ({casino.yoyChangePercent >= 0 ? "+" : ""}{casino.yoyChangePercent.toFixed(1)}% YoY)
                                        </span>
                                      )}
                                    </span>
                                  ) : (
                                    <span>
                                      {getYearOverYearChange(casino) >= 0 ? "+" : ""}
                                      {getYearOverYearChange(casino).toFixed(1)}% YoY
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="py-4 px-6 text-right font-medium text-blue-400">
                                {formatCurrency(casino.totalSlotWin || 0)}
                                <div className="text-[11px] text-gray-400 font-normal">
                                  {casino.slotHold || 0}% hold
                                </div>
                              </td>
                              <td className="py-4 px-6 text-right font-medium text-orange-400">
                                {formatCurrency(casino.totalTableWin || 0)}
                                <div className="text-[11px] text-gray-400 font-normal">
                                  {casino.tableHold || 0}% hold
                                </div>
                              </td>
                            </>
                          )}

                          <td className="py-4 px-4 text-center text-gray-300 font-medium">
                            {formatNumber(casino.gameCount || 0)}
                          </td>
                          <td
                            className="py-4 px-4 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={() => toggleCompareCasino(casino.id)}
                              className={`p-1.5 rounded-lg border text-xs inline-flex items-center justify-center transition-colors ${
                                comparisonIds.includes(casino.id)
                                  ? "bg-purple-600/30 border-purple-500 text-purple-300"
                                  : "bg-gray-900 border-gray-700 text-gray-400 hover:text-white"
                              }`}
                              title="Toggle side-by-side comparison"
                            >
                              <Scale className="h-3.5 w-3.5" />
                            </button>
                          </td>
                          <td className="py-4 px-6 text-right">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCasino(casino);
                              }}
                              className="px-3.5 py-1.5 bg-gray-800 hover:bg-purple-600 text-gray-300 hover:text-white rounded-lg text-xs font-semibold transition-colors border border-gray-700 hover:border-purple-500"
                            >
                              Details →
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {isDemo && (
                  <div className="p-8 rounded-3xl bg-gradient-to-br from-purple-950/40 via-gray-900 to-blue-950/40 border border-purple-500/40 text-center relative overflow-hidden backdrop-blur-md shadow-2xl">
                    <div className="max-w-xl mx-auto">
                      <div className="w-12 h-12 rounded-2xl bg-purple-500/20 text-purple-300 flex items-center justify-center mx-auto mb-3 border border-purple-500/30">
                        <ShieldCheck className="h-6 w-6" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-2">
                        Unlock Full Historical Gaming Tables
                      </h3>
                      <p className="text-sm text-gray-300 mb-6">
                        Viewing 3 of 500+ commercial properties. Join Casino Figures to access multi-state regulatory reports, complete hold rates, and raw CSV downloads.
                      </p>
                      <button
                        onClick={onSignUp}
                        className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold rounded-xl shadow-lg shadow-purple-500/25 transition-all text-sm inline-flex items-center gap-2"
                      >
                        <span>Start Free Trial / Join</span>
                        <ArrowUpRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* VIEW MODE 3: SIDE-BY-SIDE COMPARISON */}
            {viewMode === "compare" && (
              <div className="space-y-8 animate-fadeIn">
                {/* Selector Chips Bar */}
                <div className="bg-gray-800/60 backdrop-blur-md rounded-2xl p-6 border border-gray-800 shadow-xl">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Scale className="h-5 w-5 text-purple-400" />
                        Side-by-Side Comparison Matrix
                      </h3>
                      <p className="text-xs text-gray-400 mt-1">
                        Choose up to 3 casinos below to compare revenue
                        velocity, hold rates, and game offerings.
                      </p>
                    </div>
                    <span className="text-xs px-3 py-1 bg-purple-900/60 border border-purple-700/50 text-purple-300 rounded-full font-semibold">
                      {comparisonIds.length} of 3 Selected
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {filteredData.map((casino) => {
                      const isSelected = comparisonIds.includes(casino.id);
                      return (
                        <button
                          key={casino.id}
                          onClick={() => toggleCompareCasino(casino.id)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                            isSelected
                              ? "bg-purple-600 border-purple-500 text-white shadow-md shadow-purple-600/30"
                              : "bg-gray-900/80 border-gray-700 text-gray-300 hover:border-gray-500"
                          }`}
                        >
                          {isSelected ? (
                            <Check className="h-3.5 w-3.5 text-white" />
                          ) : (
                            <Plus className="h-3.5 w-3.5 text-gray-400" />
                          )}
                          {casino.casino}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Comparative Bar Chart */}
                {comparisonCasinos.length > 0 && (
                  <div className="bg-gray-800/60 backdrop-blur-md rounded-2xl p-6 border border-gray-800 shadow-xl">
                    <h3 className="text-base font-bold text-white mb-4">
                      {perspective === "player"
                        ? "Comparative Player Payouts ($ Millions)"
                        : "Comparative Financial Breakdown ($ Millions)"}
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={comparisonChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="name" stroke="#9CA3AF" />
                        <YAxis
                          stroke="#9CA3AF"
                          tickFormatter={(val) => `$${val}M`}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#111827",
                            border: "1px solid #374151",
                            borderRadius: "12px",
                          }}
                          labelStyle={{ color: "#F9FAFB", fontWeight: "bold" }}
                          formatter={(val) => [`$${val}M`]}
                        />
                        <Legend wrapperStyle={{ paddingTop: "10px" }} />
                        {perspective === "player" ? (
                          <>
                            <Bar
                              dataKey="Paid to Players"
                              name="Total Paid to Players"
                              fill="#10B981"
                              radius={[6, 6, 0, 0]}
                            />
                            <Bar
                              dataKey="Slots Won by Players"
                              name="Slot Machine Prizes"
                              fill="#3B82F6"
                              radius={[6, 6, 0, 0]}
                            />
                            <Bar
                              dataKey="Tables Won by Players"
                              name="Table Game Prizes"
                              fill="#F97316"
                              radius={[6, 6, 0, 0]}
                            />
                          </>
                        ) : (
                          <>
                            <Bar
                              dataKey="Revenue"
                              name="Total Revenue"
                              fill="#8B5CF6"
                              radius={[6, 6, 0, 0]}
                            />
                            <Bar
                              dataKey="Slots"
                              name="Slots Win"
                              fill="#3B82F6"
                              radius={[6, 6, 0, 0]}
                            />
                            <Bar
                              dataKey="Tables"
                              name="Tables Win"
                              fill="#F97316"
                              radius={[6, 6, 0, 0]}
                            />
                          </>
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Comparison Columns */}
                <div
                  className={`grid grid-cols-1 md:grid-cols-${Math.min(
                    comparisonCasinos.length,
                    3
                  )} gap-6`}
                >
                  {comparisonCasinos.map((casino) => (
                    <div
                      key={casino.id}
                      className="bg-gray-800/50 backdrop-blur-md rounded-2xl border border-purple-500/40 p-6 flex flex-col justify-between shadow-xl"
                    >
                      <div>
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="text-xl font-bold text-white mb-1">
                              {casino.casino}
                            </h4>
                            <p className="text-xs text-gray-400">
                              {casino.city}, {casino.state}
                            </p>
                          </div>
                          <div className="flex items-center text-yellow-400 bg-yellow-400/10 px-2 py-1 rounded-lg text-xs font-semibold">
                            <Star className="h-3.5 w-3.5 fill-yellow-400 mr-1" />
                            {casino.rating}
                          </div>
                        </div>

                        <div className="space-y-4 my-6">
                          {perspective === "player" ? (
                            <div className="p-4 bg-gray-900/60 rounded-xl border border-emerald-500/30">
                              <div className="text-xs text-gray-400 mb-1">
                                Slot Payback (RTP)
                              </div>
                              <div className="text-2xl font-extrabold text-emerald-400">
                                {casino.slotRtp ? `${casino.slotRtp}%` : "N/A"}
                              </div>
                              <div className="text-xs text-emerald-300/80 mt-1 font-medium">
                                {casino.slotRtp ? `Returns $${casino.slotRtp} per $100 wagered` : ""}
                              </div>
                            </div>
                          ) : (
                            <div className="p-4 bg-gray-900/60 rounded-xl border border-gray-700/40">
                              <div className="text-xs text-gray-400 mb-1">
                                Total Revenue
                              </div>
                              <div className="text-2xl font-extrabold text-green-400">
                                {formatCurrency(casino.totalRevenue)}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                {selectedTimePeriod.includes("-") ? (
                                  <span>
                                    <span className={casino.momChangePercent >= 0 ? "text-green-400 font-medium" : "text-red-400 font-medium"}>
                                      {casino.momChangePercent !== null && casino.momChangePercent !== undefined ? `${casino.momChangePercent >= 0 ? "+" : ""}${casino.momChangePercent.toFixed(1)}% vs prior month` : ""}
                                    </span>
                                    {casino.yoyChangePercent !== null && casino.yoyChangePercent !== undefined && (
                                      <span className="text-gray-400 ml-1.5 block">
                                        ({casino.yoyChangePercent >= 0 ? "+" : ""}{casino.yoyChangePercent.toFixed(1)}% YoY)
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span>
                                    {getYearOverYearChange(casino) >= 0 ? "+" : ""}
                                    {getYearOverYearChange(casino).toFixed(1)}% vs prior year
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="space-y-2 text-sm">
                            {perspective === "player" ? (
                              <>
                                <div className="flex justify-between py-2 border-b border-gray-700/50">
                                  <span className="text-gray-400">Paid to Slot Players:</span>
                                  <span className="text-blue-400 font-semibold">
                                    {formatCurrency(casino.slotPayout || 0)}
                                  </span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-gray-700/50">
                                  <span className="text-gray-400">Slot Handle (Total Bet):</span>
                                  <span className="text-white font-semibold">
                                    {formatCurrency(casino.totalSlotHandle || 0)}
                                  </span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-gray-700/50">
                                  <span className="text-gray-400">Table Player Return:</span>
                                  <span className="text-amber-400 font-semibold">
                                    {casino.tableRtp ? `${casino.tableRtp}%` : "N/A"}
                                  </span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-gray-700/50">
                                  <span className="text-gray-400">Table Chips Cashed:</span>
                                  <span className="text-orange-400 font-semibold">
                                    {formatCurrency(casino.tablePayout || 0)}
                                  </span>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="flex justify-between py-2 border-b border-gray-700/50">
                                  <span className="text-gray-400">Slots Win:</span>
                                  <span className="text-blue-400 font-semibold">
                                    {formatCurrency(casino.totalSlotWin)}
                                  </span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-gray-700/50">
                                  <span className="text-gray-400">
                                    Slot Hold %:
                                  </span>
                                  <span className="text-white font-semibold">
                                    {casino.slotHold || casino.totalSlotWinPercent}%
                                  </span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-gray-700/50">
                                  <span className="text-gray-400">Tables Win:</span>
                                  <span className="text-orange-400 font-semibold">
                                    {formatCurrency(casino.totalTableWin)}
                                  </span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-gray-700/50">
                                  <span className="text-gray-400">
                                    Table Hold %:
                                  </span>
                                  <span className="text-white font-semibold">
                                    {casino.tableHold || casino.totalTableWinPercent}%
                                  </span>
                                </div>
                              </>
                            )}
                            <div className="flex justify-between py-2 border-b border-gray-700/50">
                              <span className="text-gray-400">Poker Win:</span>
                              <span className="text-purple-400 font-semibold">
                                {formatCurrency(casino.totalPokerWin)}
                              </span>
                            </div>
                            <div className="flex justify-between py-2">
                              <span className="text-gray-400">
                                Gaming Units:
                              </span>
                              <span className="text-white font-semibold">
                                {formatNumber(casino.gameCount)} games
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => setSelectedCasino(casino)}
                        className="w-full py-2.5 bg-gray-900 hover:bg-purple-600 text-gray-300 hover:text-white font-semibold rounded-xl text-xs transition-colors border border-gray-700"
                      >
                        Inspect Full Profile →
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Casino Detail Modal */}
      {selectedCasino && (
        <CasinoDetailModal
          casino={selectedCasino}
          onClose={() => setSelectedCasino(null)}
        />
      )}

      {/* User Profile & Account Settings Modal */}
      <ProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        onSignOut={() => {
          setProfileModalOpen(false);
          onBackToLanding();
        }}
      />


      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col items-center justify-center text-center">
            <h3 className="text-xl font-bold text-white mb-2">
              Casino Figures
            </h3>
            <p className="text-gray-400 text-sm max-w-xl mx-auto mb-6">
              Official gaming performance, hold rate metrics, and financial reporting intelligence compiled from regulatory commission filings.
            </p>

            {/* Legal & Compliance Links */}
            <div className="flex flex-wrap justify-center items-center gap-y-2 gap-x-6 text-sm mb-6">
              <button
                type="button"
                onClick={() => {
                  setLegalModalTab("terms");
                  setLegalModalOpen(true);
                }}
                className="text-gray-400 hover:text-purple-400 font-medium transition-colors"
              >
                Terms & Conditions
              </button>
              <span className="text-gray-700 hidden sm:inline">•</span>
              <button
                type="button"
                onClick={() => {
                  setLegalModalTab("privacy");
                  setLegalModalOpen(true);
                }}
                className="text-gray-400 hover:text-blue-400 font-medium transition-colors"
              >
                Privacy Policy
              </button>
              <span className="text-gray-700 hidden sm:inline">•</span>
              <button
                type="button"
                onClick={() => {
                  setLegalModalTab("responsible");
                  setLegalModalOpen(true);
                }}
                className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors flex items-center gap-1.5"
              >
                <span>Responsible Gaming</span>
                <span className="px-1.5 py-0.2 bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold rounded">
                  21+
                </span>
              </button>
              <span className="text-gray-700 hidden sm:inline">•</span>
              <button
                type="button"
                onClick={() => {
                  setLegalModalTab("disclaimer");
                  setLegalModalOpen(true);
                }}
                className="text-gray-400 hover:text-amber-400 font-medium transition-colors"
              >
                Data & Regulatory Disclaimer
              </button>
            </div>

            <p className="text-gray-500 text-xs max-w-2xl mx-auto leading-relaxed">
              © 2026 Casino Figures. All rights reserved. Casino Figures is an independent data aggregator and analytics platform. Historical Return to Player (RTP %) and casino hold rates are derived from public regulatory records and do not guarantee future gaming outcomes.
            </p>
          </div>
        </div>
      </footer>

      {/* Legal & Compliance Modal */}
      <LegalModal
        isOpen={legalModalOpen}
        initialTab={legalModalTab}
        onClose={() => setLegalModalOpen(false)}
      />
    </div>
  );
}

export default CasinoFigures;
